import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Persistent top banner shown to admins while they are impersonating an
 * investor. Read-only mode — backend rejects any non-GET request while the
 * X-Impersonate-Email header is active.
 */
export const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const impersonateEmail = typeof window !== 'undefined'
    ? localStorage.getItem('impersonate_email')
    : null;

  if (!impersonateEmail) return null;

  const exitImpersonation = async () => {
    localStorage.removeItem('impersonate_email');
    // Refresh auth context so sidebar/role flips back to the real admin
    await checkAuth(true);
    navigate('/settings');
  };

  // Prefer the impersonated user's name if AuthContext has rehydrated to them
  const displayName = user?._impersonated
    ? (user.name || user.email || impersonateEmail)
    : impersonateEmail;

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 w-full bg-purple-600 text-white shadow-md"
    >
      <div className="px-4 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium truncate">
            Viewing as <span className="font-semibold">{displayName}</span>
            <span className="hidden sm:inline text-purple-100 font-normal ml-2">
              ({impersonateEmail}) — read-only admin view
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={exitImpersonation}
          data-testid="exit-impersonation-btn"
          className="flex items-center gap-1.5 px-3 py-1 bg-white/15 hover:bg-white/25 rounded-sm text-xs font-medium transition-colors flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
          Exit Impersonation
        </button>
      </div>
    </div>
  );
};
